import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Archive, Briefcase, CalendarDays, Copyright, FileStack, Gavel, Inbox, Loader2, Lock, LockOpen, Mail, Mic, Newspaper, Search, Shapes, UserPlus, Users } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "@/lib/api";
import { fmtDateTime } from "@/lib/time";
import { useAdmin } from "@/context/AdminContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { StatusPill } from "@/components/submissions/shared";
import { SecurityCheck } from "@/components/submissions/SecurityCheck";
import { SubmissionView } from "@/components/submissions/SubmissionView";

const ICONS = {
  "General Inquiries": Mail, "Partnerships & Business Opportunities": Briefcase, "Guest Appearances": Mic,
  "Events & Premieres Invitations": CalendarDays, "Press & Media Inquiries": Newspaper, "Usage Rights & Licencing Inquiries": Copyright, "Submission Copy or Deletion Requests": FileStack, Other: Shapes,
};
const CLOSED_KEY = "__closed";
const INBOX_KEY = "__inbox";
const MINE_KEY = "__mine";
const APPEALS_KEY = "__appeals";
const TEAM_KEY = "__team";
const DATA_CATEGORY = "Submission Copy or Deletion Requests";
const APPEAL_STATUSES = ["appeal_submitted", "appeal_review"];
const asList = (r) => (Array.isArray(r?.data) ? r.data : []);
const slug = (t) => t.toLowerCase().replace(/[^a-z]+/g, "-");

const useList = (url, params) => {
  const [items, setItems] = useState(null);
  const key = JSON.stringify(params || {});
  const load = useCallback(() => {
    api.get(url, { params: JSON.parse(key) }).then((r) => setItems(asList(r))).catch((e) => { toast.error(errText(e)); setItems([]); });
  }, [url, key]);
  useEffect(() => { load(); return undefined; }, [load]);
  return [items, load, setItems];
};

const BigCard = ({ k, title, text, Icon, count, countLabel, tone = "light", onPick }) => {
  const tones = {
    light: ["border border-black/15 bg-white hover:border-[#0A0A0A]", "bg-[#0A0A0A] text-white", "bg-[#E62314]", "text-[#57534E]"],
    amber: ["border-2 border-[#B45309] bg-[#FFF4E5] hover:bg-[#FFEBCC]", "bg-[#B45309] text-white", "bg-[#B45309]", "text-[#57534E]"],
    violet: ["border-2 border-[#6D28D9] bg-[#F5F0FF] hover:bg-[#EDE4FF]", "bg-[#6D28D9] text-white", "bg-[#6D28D9]", "text-[#57534E]"],
    dark: ["border border-black/15 bg-[#0A0A0A] text-white hover:border-[#E62314]", "bg-white text-[#0A0A0A]", "bg-[#E62314]", "text-white/70"],
  }[tone];
  return (
    <button onClick={() => onPick(k)} className={`group relative flex w-full items-center gap-4 overflow-hidden p-5 text-left transition-colors ${tones[0]}`} data-testid={`hub-${slug(k)}`}>
      <span className="absolute inset-x-0 bottom-0 h-1 origin-left scale-x-0 bg-[#E62314] transition-transform duration-300 group-hover:scale-x-100" />
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center ${tones[1]}`}><Icon className="h-5 w-5" /></span>
      <div className="min-w-0 flex-1">
        <p className="font-display text-lg font-extrabold leading-tight">{title}</p>
        <p className={`mt-1 text-xs ${tones[3]}`}>{text}</p>
      </div>
      {count > 0 && <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-bold text-white ${tones[2]}`} data-testid={`hub-${slug(k)}-count`}>{count} {countLabel}</span>}
      <ArrowRight className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-1" />
    </button>
  );
};

const QueuePicker = ({ queues, counts, isAdmin, onPick }) => {
  const data = queues.find((q) => q.category === DATA_CATEGORY);
  return (
    <div data-testid="queue-picker">
      <div className="grid gap-3 md:grid-cols-2">
        <BigCard k={MINE_KEY} title="My inbox" text={`Submissions assigned to you. Only you can deal with these.${counts.offers ? ` ${counts.offers} waiting for you to accept.` : ""}`} Icon={Inbox} count={counts.mine + counts.offers} countLabel={counts.offers ? `· ${counts.offers} to accept` : "active"} onPick={onPick} />
        <BigCard k={APPEALS_KEY} title="Appeals" text="Appeals from every queue. Someone who didn't handle the submission before must pick each one up." Icon={Gavel} tone="violet" count={counts.appeals} countLabel="waiting" onPick={onPick} />
        <BigCard k={INBOX_KEY} title="Messages inbox" text="People chasing up their submission. Not a queue, newest first." Icon={Mail} tone="amber" count={counts.messages} countLabel="new" onPick={onPick} />
        {isAdmin && <BigCard k={TEAM_KEY} title="Team inboxes" text="Owner only. See what's in each team member's inbox and step in if needed." Icon={Users} onPick={onPick} />}
      </div>
      <p className="mt-8 text-xs font-bold uppercase tracking-[0.2em] text-[#57534E]">Queues</p>
      <p className="mt-1 max-w-xl text-sm text-[#57534E]">Each inquiry type from the contact form has its own queue. Queues only hold submissions nobody has assigned to themselves yet.</p>
      <ul className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {queues.filter((q) => q.category !== DATA_CATEGORY).map((q, i) => {
          const Icon = ICONS[q.category] || Shapes;
          return (
            <motion.li key={q.category} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
              <button onClick={() => onPick(q.category)} className="group relative flex h-full w-full flex-col overflow-hidden border border-black/15 bg-white p-5 text-left transition-colors hover:border-[#0A0A0A]" data-testid={`queue-${slug(q.category)}`}>
                <span className="absolute inset-x-0 bottom-0 h-1 origin-left scale-x-0 bg-[#E62314] transition-transform duration-300 group-hover:scale-x-100" />
                <div className="flex items-start justify-between gap-3">
                  <span className="flex h-10 w-10 items-center justify-center bg-[#0A0A0A] text-white"><Icon className="h-5 w-5" /></span>
                  {q.queue > 0 && <span className="rounded-full bg-[#E62314] px-2.5 py-0.5 text-xs font-bold text-white">{q.queue} in queue</span>}
                </div>
                <p className="mt-4 break-words font-display text-lg font-extrabold leading-tight">{q.category}</p>
                <p className="mt-1 text-xs text-[#57534E]">{q.queue ? `${q.queue} waiting to be assigned` : "Nothing waiting"}</p>
                <span className="mt-4 flex items-center gap-1 text-xs font-bold">Open queue<ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" /></span>
              </button>
            </motion.li>
          );
        })}
      </ul>
      {data && (
        <section className="mt-8" data-testid="data-requests-section">
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-[#57534E]">Copy &amp; deletion requests</p>
          <BigCard k={DATA_CATEGORY} title="Submission Copy or Deletion Requests" text="People asking for a copy of their closed submission or for it to be deleted" Icon={FileStack} count={data.queue} countLabel="waiting" onPick={onPick} />
        </section>
      )}
      <div className="mt-3"><BigCard k={CLOSED_KEY} title="Closed" text={`Every closed submission from all queues · ${counts.closed} total`} Icon={Archive} tone="dark" onPick={onPick} /></div>
    </div>
  );
};

const Shell = ({ title, text, backLabel = "Back to submissions", onBack, testId, children }) => (
  <div data-testid={testId}>
    <button onClick={onBack} className="flex items-center gap-1.5 text-sm font-semibold hover:text-[#E62314]" data-testid="queue-back"><ArrowLeft className="h-4 w-4" />{backLabel}</button>
    <h3 className="mt-3 break-words font-display text-2xl font-extrabold">{title}</h3>
    {text && <p className="mt-1 max-w-2xl text-xs text-[#57534E]">{text}</p>}
    {children}
  </div>
);

const ListBox = ({ items, empty, children }) => (
  <div className="mt-4 border border-black/15 bg-white">
    {!items ? <div className="h-24 animate-pulse bg-black/5" /> : !items.length ? <p className="p-8 text-center text-sm text-[#57534E]" data-testid="queue-empty">{empty}</p> : <ul className="divide-y divide-black/5">{children}</ul>}
  </div>
);

const Row = ({ s, n, meta, onOpen, action }) => (
  <li className="flex flex-col gap-3 px-5 py-4 hover:bg-[#F3F2EF] sm:flex-row sm:items-center sm:gap-6" data-testid={`queue-row-${s.ref}`}>
    <button onClick={() => onOpen(s.id)} className="flex min-w-0 flex-1 items-start gap-4 text-left sm:items-center" data-testid={`queue-item-${s.ref}`}>
      {n && <span className="hidden w-6 shrink-0 font-display text-lg font-extrabold text-[#57534E] sm:block">{n}</span>}
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-baseline gap-x-3"><span className="font-mono text-xs tracking-widest text-[#57534E]">{s.ref}</span><span className="break-words text-sm font-bold">{s.name}</span>{s.category && <span className="text-xs text-[#57534E]">{s.category}</span>}</p>
        <p className="mt-1 line-clamp-1 break-words text-sm text-[#3a3836]">{s.preview || s.message}</p>
        <p className="mt-1 text-xs text-[#57534E]">{meta}</p>
      </div>
    </button>
    <div className="flex flex-wrap items-center gap-2">
      {s.chase_open && <span className="rounded-full bg-[#FFF4E5] px-2.5 py-0.5 text-xs font-bold text-[#B45309]">Chased up</span>}
      <StatusPill status={s.status} />
      {action}
    </div>
  </li>
);

const ClaimDialog = ({ item, isAdmin, onClose, onOpen, onGone }) => {
  const [busy, setBusy] = useState("");
  const [team, setTeam] = useState([]);
  const [to, setTo] = useState("");
  const [note, setNote] = useState("");
  const [notify, setNotify] = useState(false);
  useEffect(() => {
    if (!item) return undefined;
    setTo(""); setNote(""); setNotify(false);
    if (isAdmin) api.get("/queues/team").then((r) => setTeam(asList(r))).catch(() => setTeam([]));
    return undefined;
  }, [item, isAdmin]);
  const call = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "me") { await api.post(`/queues/items/${item.id}/assign`, { notify }); toast.success(`${item.ref} is now in your inbox${notify ? " and they've been emailed" : ""}`); onOpen(item.id); }
      else { const r = await api.post(`/queues/items/${item.id}/transfer`, { to, note: note.trim(), notify }); toast.success(`Sent to ${r.data?.to_name}. It moves to their inbox once they accept`); onGone(item.id); }
    } catch (e) { toast.error(errText(e)); onGone(null); } finally { setBusy(""); }
  };
  const mayClaim = item?.can_claim !== false;
  const btn = "flex w-full items-center justify-center gap-1.5 py-3 text-sm font-bold transition-colors disabled:opacity-40";
  const field = "w-full border border-black/20 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#0A0A0A]";
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-none" data-testid="claim-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold">{isAdmin ? "Who should handle this?" : "Assign this to yourself?"}</DialogTitle>
          <DialogDescription className="text-sm text-[#57534E]">
            {item && <span className="block font-mono text-xs tracking-widest text-[#0A0A0A]">{item.ref} · {item.name}</span>}
            {isAdmin ? "Assign it to yourself or to someone on the team. It moves into that person's inbox and only they can deal with it." : "You need to assign a submission to yourself before you can open it. It moves into your inbox and nobody else on the team can open it."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="flex items-start justify-between gap-3 border border-black/15 bg-[#FBF9F5] p-3 text-sm font-semibold">
            <span>Would you like to send an email to this submitter?<span className="mt-0.5 block text-xs font-normal text-[#57534E]">It tells them who will be looking after their {APPEAL_STATUSES.includes(item?.status) ? "appeal" : "submission"}.</span></span>
            <Switch checked={notify} onCheckedChange={setNotify} data-testid="claim-notify" />
          </label>
          {mayClaim
            ? <button disabled={!!busy} onClick={() => call("me")} className={`${btn} bg-[#0A0A0A] text-white hover:bg-[#E62314]`} data-testid="claim-assign-me">{busy === "me" ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}Assign to me and open</button>
            : <p className="text-xs font-semibold text-[#6D28D9]" data-testid="claim-excluded">You handled this submission before its appeal, so it has to go to someone independent.</p>}
          {isAdmin && (
            <div className="border-t border-black/10 pt-3" data-testid="claim-someone-else">
              <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-[#57534E]">Or assign to someone else</p>
              <p className="mb-2 text-xs text-[#57534E]">They get an email and have to accept it before it moves into their inbox.</p>
              <select value={to} onChange={(e) => setTo(e.target.value)} className={field} data-testid="claim-to">
                <option value="">Choose a team member</option>
                {team.filter((u) => !u.owner).map((u) => <option key={u.id} value={u.id}>{u.name}{u.job_title ? ` · ${u.job_title}` : ""} ({u.inbox} active)</option>)}
              </select>
              <textarea rows={2} value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} placeholder="Note for them (optional, only the team sees it)" className={`${field} mt-2`} data-testid="claim-note" />
              <button disabled={!to || !!busy} onClick={() => call("other")} className={`${btn} mt-2 border border-black/80 hover:bg-black hover:text-white`} data-testid="claim-assign-other">{busy === "other" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Users className="h-4 w-4" />}Assign to them</button>
              <button onClick={() => onOpen(item.id)} className="mt-3 w-full text-center text-xs font-semibold text-[#57534E] underline-offset-4 hover:text-[#E62314] hover:underline" data-testid="claim-just-open">Just open it without assigning</button>
            </div>
          )}
          {!isAdmin && <button onClick={onClose} className={`${btn} border border-black/15 hover:border-black`} data-testid="claim-cancel">Not now</button>}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ClaimList = ({ title, text, url, params, empty, numbered, appeals, onBack, onOpen, backLabel }) => {
  const { isAdmin } = useAdmin();
  const [items, load, setItems] = useList(url, params);
  const [pick, setPick] = useState(null);
  const gone = (id) => { setPick(null); if (id) setItems((x) => (x || []).filter((s) => s.id !== id)); else load(); };
  return (
    <Shell title={title} text={text} onBack={onBack} backLabel={backLabel} testId={appeals ? "appeals-list" : "queue-list"}>
      <ListBox items={items} empty={empty}>
        {(items || []).map((s, i) => (
          <Row key={s.id} s={s} n={numbered ? i + 1 : null} onOpen={() => setPick(s)} meta={`Received ${fmtDateTime(s.created_at)}${appeals ? ` · appealed ${fmtDateTime(s.updated_at)}` : ""}`}
            action={<span className="flex items-center gap-1 text-xs font-bold"><Lock className="h-3.5 w-3.5" />{appeals && !s.can_claim ? "You handled this before" : "Assign to open"}</span>} />
        ))}
      </ListBox>
      <ClaimDialog item={pick} isAdmin={isAdmin} onClose={() => setPick(null)} onGone={gone} onOpen={(id) => { setPick(null); onOpen(id); }} />
    </Shell>
  );
};

const OfferRow = ({ s, onDone }) => {
  const [busy, setBusy] = useState("");
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState("");
  const go = async (kind) => {
    setBusy(kind);
    try { await api.post(`/queues/items/${s.id}/transfer/${kind}`, kind === "decline" ? { note: reason.trim() } : {}); toast.success(kind === "accept" ? `${s.ref} is now in your inbox` : `Declined. ${s.offer?.by_name || "They"} will be told`); onDone(); }
    catch (e) { toast.error(errText(e)); onDone(); } finally { setBusy(""); }
  };
  const btn = "flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold transition-colors disabled:opacity-40";
  return (
    <li className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:gap-6" data-testid={`offer-${s.ref}`}>
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-baseline gap-x-3"><span className="font-mono text-xs tracking-widest text-[#57534E]">{s.ref}</span><span className="break-words text-sm font-bold">{s.name}</span><span className="text-xs text-[#57534E]">{s.category}</span></p>
        <p className="mt-1 line-clamp-1 break-words text-sm text-[#3a3836]">{s.preview}</p>
        <p className="mt-1 text-xs text-[#57534E]">{s.offer?.by_name} wants to pass you this · {fmtDateTime(s.offer?.at)}</p>
        {s.offer?.note && <p className="mt-1 whitespace-pre-wrap break-words text-xs font-semibold">Note: {s.offer.note}</p>}
        {declining && (
          <div className="mt-3" data-testid={`offer-decline-form-${s.ref}`}>
            <textarea rows={2} value={reason} maxLength={1000} onChange={(e) => setReason(e.target.value)} placeholder={`Reason for ${s.offer?.by_name || "them"} (optional), e.g. I'm not the right person for this`} className="w-full border border-black/20 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0A0A]" data-testid={`offer-decline-reason-${s.ref}`} />
            <div className="mt-2 flex flex-wrap gap-2">
              <button onClick={() => { setDeclining(false); setReason(""); }} className="rounded-full border border-black/20 px-4 py-2 text-xs font-bold hover:border-black" data-testid={`offer-decline-back-${s.ref}`}>Back</button>
              <button disabled={!!busy} onClick={() => go("decline")} className="flex items-center gap-1.5 rounded-full bg-[#E62314] px-4 py-2 text-xs font-bold text-white hover:bg-[#0A0A0A] disabled:opacity-40" data-testid={`offer-decline-send-${s.ref}`}>{busy === "decline" && <Loader2 className="h-3.5 w-3.5 animate-spin" />}Decline hand-over</button>
            </div>
          </div>
        )}
      </div>
      {!declining && <div className="flex flex-wrap items-center gap-2">
        <StatusPill status={s.status} />
        <button disabled={!!busy} onClick={() => setDeclining(true)} className={`${btn} border border-black/20 hover:border-black`} data-testid={`offer-decline-${s.ref}`}>Decline</button>
        <button disabled={!!busy} onClick={() => go("accept")} className={`${btn} bg-[#0A0A0A] text-white hover:bg-[#E62314]`} data-testid={`offer-accept-${s.ref}`}>{busy === "accept" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />}Accept</button>
      </div>}
    </li>
  );
};

const Offers = ({ onAccepted }) => {
  const [items, load] = useList("/queues/offers");
  if (!items?.length) return null;
  return (
    <section className="mt-5 border-2 border-[#B45309] bg-[#FFF4E5]" data-testid="offers">
      <p className="px-5 pt-4 text-xs font-bold uppercase tracking-[0.2em] text-[#B45309]">Waiting for you to accept · {items.length}</p>
      <ul className="divide-y divide-black/5">{items.map((s) => <OfferRow key={s.id} s={s} onDone={() => { load(); onAccepted(); window.dispatchEvent(new Event("cm-offers")); }} />)}</ul>
    </section>
  );
};

const MyInbox = ({ person, onBack, onOpen }) => {
  const [items, load] = useList("/queues/mine", person ? { user_id: person.id } : undefined);
  return (
    <Shell title={person ? `${person.name}'s inbox` : "My inbox"} backLabel={person ? "Back to team inboxes" : "Back to submissions"} onBack={onBack} testId="my-inbox"
      text={person ? "Owner view. Everything currently assigned to this team member. You can open any of them and pass them on." : "Only you can open and deal with these. Pass one to a colleague from inside the submission if someone else should handle it. Most recently updated at the top."}>
      {!person && <Offers onAccepted={load} />}
      <ListBox items={items} empty={person ? "Their inbox is empty." : "Your inbox is empty. Assign a submission to yourself from a queue or from Appeals."}>
        {(items || []).map((s) => <Row key={s.id} s={s} onOpen={onOpen} meta={`Updated ${fmtDateTime(s.updated_at || s.created_at)} · in inbox since ${fmtDateTime(s.assigned_at)}`} />)}
      </ListBox>
    </Shell>
  );
};

const TeamInboxes = ({ onBack, onPickPerson }) => {
  const [items] = useList("/queues/team");
  return (
    <Shell title="Team inboxes" text="Everyone who can access submissions. Open an inbox to see what they're handling." onBack={onBack} testId="team-inboxes">
      <ListBox items={items} empty="No team members can access submissions yet.">
        {(items || []).map((u) => (
          <li key={u.id}>
            <button onClick={() => onPickPerson(u)} className="flex w-full items-center gap-4 px-5 py-4 text-left hover:bg-[#F3F2EF]" data-testid={`team-inbox-${u.id}`}>
              <div className="min-w-0 flex-1"><p className="break-words text-sm font-bold">{u.name}{u.owner ? " (you)" : ""}</p><p className="text-xs text-[#57534E]">{u.job_title || "Team member"}</p></div>
              <span className="shrink-0 rounded-full bg-black/5 px-2.5 py-0.5 text-xs font-bold">{u.inbox} active</span>
              <ArrowRight className="h-4 w-4 shrink-0" />
            </button>
          </li>
        ))}
      </ListBox>
    </Shell>
  );
};

const MessagesList = ({ onBack, onOpen }) => {
  const { isAdmin } = useAdmin();
  const [items, load, setItems] = useList("/queues/inbox");
  const [pick, setPick] = useState(null);
  const gone = (id) => { setPick(null); if (id) setItems((x) => (x || []).filter((s) => s.id !== id)); else load(); };
  return (
    <Shell title="Messages inbox" text="Chase-ups waiting for a reply. Newest at the top. You see chase-ups on submissions in your inbox and on ones nobody has assigned yet. Assign an unassigned one to yourself to open it and reply." onBack={onBack} testId="inbox-list">
      <ListBox items={items} empty="No chase-ups waiting.">
        {(items || []).map((s) => <Row key={s.id} s={{ ...s, chase_open: false }} onOpen={() => (s.unassigned ? setPick(s) : onOpen(s.id))} meta={`Chased up ${fmtDateTime(s.chased_at)}${s.assigned_name ? ` · in ${s.assigned_name}'s inbox` : " · not assigned"}`}
          action={s.unassigned && <span className="flex items-center gap-1 text-xs font-bold"><Lock className="h-3.5 w-3.5" />Assign to open</span>} />)}
      </ListBox>
      <ClaimDialog item={pick} isAdmin={isAdmin} onClose={() => setPick(null)} onGone={gone} onOpen={(id) => { setPick(null); onOpen(id); }} />
    </Shell>
  );
};

const ClosedList = ({ onBack, onOpen }) => {
  const [locked, setLocked] = useState(null);
  const [items, load] = useList("/queues/closed");
  const [q, setQ] = useState("");
  const term = q.trim().toLowerCase();
  const shown = items && items.filter((s) => !term || (s.name || "").toLowerCase().includes(term));
  return (
    <Shell title="Closed" text="All closed submissions from every queue. Each one needs a security verification to open. Newest at the top, oldest at the bottom." onBack={onBack} testId="closed-list">
      <label className="mt-4 flex items-center gap-2 border border-black/20 bg-white px-3 focus-within:border-black">
        <Search className="h-4 w-4 shrink-0 text-[#57534E]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name" className="w-full bg-transparent py-2.5 text-sm outline-none" data-testid="closed-search" />
        {q && <button onClick={() => setQ("")} aria-label="Clear search" className="shrink-0 text-xs font-semibold hover:text-[#E62314]" data-testid="closed-search-clear">Clear</button>}
      </label>
      <ListBox items={shown} empty={term ? "No closed submissions match your search." : "No closed submissions yet."}>
        {(shown || []).map((s) => (
          <li key={s.id}>
            <button onClick={() => (s.locked ? setLocked(s) : onOpen(s.id))} className="flex w-full flex-col gap-2 px-5 py-4 text-left hover:bg-[#F3F2EF] sm:flex-row sm:items-center sm:gap-6" data-testid={`closed-item-${s.id}`}>
              <div className="min-w-0 flex-1">
                <p className="break-words text-sm font-bold">{s.name}</p>
                {s.locked
                  ? <p className="mt-1 inline-flex items-center gap-1 text-sm text-[#57534E]"><Lock className="h-3.5 w-3.5" />{s.code_pending ? "Locked · verification code sent, waiting for the code" : "Locked · security verification needed to open"}</p>
                  : <p className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-[#15803D]" data-testid={`closed-item-${s.id}-unlocked`}><LockOpen className="h-3.5 w-3.5" />Unlocked until {fmtDateTime(s.unlocked_until)}</p>}
              </div>
              <StatusPill status={s.status} />
            </button>
          </li>
        ))}
      </ListBox>
      <SecurityCheck item={locked} onClose={() => { setLocked(null); load(); }} onPassed={(id) => { setLocked(null); onOpen(id); }} />
    </Shell>
  );
};

const BACK = { [CLOSED_KEY]: "Back to Closed", [INBOX_KEY]: "Back to messages", [MINE_KEY]: "Back to my inbox", [APPEALS_KEY]: "Back to appeals", [DATA_CATEGORY]: "Back to requests", [TEAM_KEY]: "Back to their inbox" };

export const SubmissionsHub = () => {
  const { isAdmin } = useAdmin();
  const [queues, setQueues] = useState([]);
  const [category, setCategory] = useState(null);
  const [person, setPerson] = useState(null);
  const [trail, setTrail] = useState([]);
  const openId = trail[trail.length - 1] || null;
  const setOpenId = useCallback((id) => setTrail(id ? [id] : []), []);
  const handleBack = useCallback(() => setTrail((t) => t.slice(0, -1)), []);
  const push = useCallback((id) => setTrail((t) => [...t, id]), []);
  const [counts, setCounts] = useState({ mine: 0, offers: 0, appeals: 0, messages: 0, closed: 0 });
  const load = useCallback(() => {
    api.get("/queues").then((r) => setQueues(asList(r))).catch(() => {});
    [["mine", "/queues/mine"], ["offers", "/queues/offers"], ["appeals", "/queues/appeals"], ["messages", "/queues/inbox"], ["closed", "/queues/closed"]].forEach(([k, url]) => {
      api.get(url).then((r) => setCounts((c) => ({ ...c, [k]: asList(r).filter((x) => k !== "appeals" || x.can_claim !== false).length }))).catch(() => {});
    });
  }, []);
  useEffect(() => { load(); return undefined; }, [load, category, openId]);
  const home = () => { setCategory(null); setPerson(null); };
  if (openId) {
    const label = trail.length > 1 ? "Back to the request" : BACK[category] || "Back to queue";
    return <SubmissionView key={openId} id={openId} onBack={handleBack} onOpen={push} backLabel={label} />;
  }
  if (category === INBOX_KEY) return <MessagesList onBack={home} onOpen={setOpenId} />;
  if (category === CLOSED_KEY) return <ClosedList onBack={home} onOpen={setOpenId} />;
  if (category === MINE_KEY) return <MyInbox onBack={home} onOpen={setOpenId} />;
  if (category === TEAM_KEY) return person ? <MyInbox person={person} onBack={() => setPerson(null)} onOpen={setOpenId} /> : <TeamInboxes onBack={home} onPickPerson={setPerson} />;
  if (category === APPEALS_KEY) {
    return <ClaimList appeals url="/queues/appeals" title="Appeals" onBack={home} onOpen={setOpenId} empty="No appeals waiting to be picked up."
      text="Appeals from every queue, oldest first. Anyone who handled a submission before its appeal can't see or deal with it, so it goes to someone independent. Assign one to yourself to move it into your inbox." />;
  }
  if (category) {
    const isData = category === DATA_CATEGORY;
    return <ClaimList url="/queues/items" params={{ category }} title={category} numbered={!isData} onBack={home} onOpen={setOpenId} backLabel={isData ? "Back to submissions" : "All queues"}
      empty="Nothing waiting to be assigned." text={isData ? "New requests nobody has picked up yet. Assign one to yourself to open it. It then moves into your inbox." : "Submissions nobody has picked up yet, oldest at the top. You have to assign a submission to yourself before you can open it. It then moves into your inbox."} />;
  }
  return <QueuePicker queues={queues} counts={counts} isAdmin={isAdmin} onPick={setCategory} />;
};
